import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
import { supabase } from "../lib/supabase";
import { normalizeFotoContextLabel } from "../utils/fotoContextLabels";
import { resolveStorageUrl } from "../utils/storageUrl";
import type { Database, Offerte } from "../../../../types";
import { useAuthStore } from "./auth";

type FotoBovenFoto = Pick<Database["rapport_details"]["Tables"]["fotos"]["Row"], "id" | "remote_url">;
type FotoBovenFotoRow = FotoBovenFoto &
  Pick<Database["rapport_details"]["Tables"]["fotos"]["Row"], "locatie_id" | "context_label">;

function normalizeFotoBovenRow<T extends { remote_url: string | null; context_label?: string | null }>(foto: T): T {
  return {
    ...foto,
    context_label: normalizeFotoContextLabel(foto.context_label),
    remote_url: foto.remote_url ? resolveStorageUrl(foto.remote_url) : foto.remote_url,
  };
}

type LocatieWithFoto = Database["public"]["Tables"]["locaties"]["Row"] & {
  fotoBoven_foto?: FotoBovenFoto[] | null;
  opdrachtgever?: Database["public"]["Tables"]["opdrachtgevers"]["Row"] & {
    user?: Database["public"]["Tables"]["users"]["Row"];
  };
};

type OfferteListRow = Omit<Offerte, "locaties" | "locatie"> & {
  locaties?: LocatieWithFoto | null;
  locatie?: LocatieWithFoto | null;
};

type LocatieOpdrachtgeverJoin = {
  locaties_opdrachtgevers?: Array<{
    is_default: boolean | null;
    opdrachtgever?: Database["public"]["Tables"]["opdrachtgevers"]["Row"] & {
      user?: Database["public"]["Tables"]["users"]["Row"];
    };
  }> | null;
};

type OfferteDetailRow = Omit<Offerte, "locatie"> & {
  locatie?: (LocatieWithFoto & LocatieOpdrachtgeverJoin) | null;
};

export const useOfferteStore = defineStore("offertes", () => {
  // shallowRef: the list is always reassigned wholesale (never mutated in place),
  // and a deep ref would force TS to instantiate UnwrapRef<Offerte> — an expensive
  // recursion over Offerte's nested join shapes that pushes consumers (Offertes.vue)
  // past the type-instantiation ceiling. shallowRef keeps store.offertes as a clean Offerte[].
  const offertes = shallowRef<Offerte[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const totalCount = ref(0);
  const authStore = useAuthStore();

  const fetchOffertes = async (searchQuery = "", searchField = "") => {
    try {
      loading.value = true;
      error.value = null;

      const listSelect: string = `
          id,
          offerteNr,
          offerteDatum,
          createdTime,
          korting,
          kortingBedrag,
          kortingPerc,
          totaalIncKorting,
          handTekening,
          basis,
          status,
          offerte_producten(
            product_type,
            voorgestelde_prijs,
            aangepaste_prijs,
            aantal,
            opmerking,
            uitvoer_datum
          ),
          voorwaarden,
          iplan,
          bundel_id,
          offerte_bundels:bundel_id(id, naam, bundel_nr),
          locaties!inner(
            id,
            naam,
            contactNaam,
            straat,
            huisnr,
            postcode,
            plaats,
            contactMail,
            telnr,
            typeObject,
            bouwJaar,
            installatieJaar,
            pvInstallatieJaar,
            gebruiksDoel,
            branche
          ),
          ibedrijf:ibedrijf_id(
            id,
            contactnaam,
            logo,
            users!inner(
              display_name
            )
          )
        `;

      let query = supabase.from("offertes").select(listSelect, { count: "exact" });

      if (searchQuery && searchField) {
        query = query.ilike(searchField, `%${searchQuery}%`);
      }

      const { data, error: queryError, count } = await query.order("createdTime", { ascending: false });

      if (queryError) throw queryError;

      totalCount.value = count || 0;
      const mappedData =
        (data as unknown as OfferteListRow[] | null)?.map((offerte) => {
          const products = Array.isArray((offerte as any).offerte_producten)
            ? (offerte as any).offerte_producten.flatMap((product: any) => {
                const rows = [
                  {
                    productType: product.product_type,
                    prijsType: "voorgesteld",
                    prijs: product.voorgestelde_prijs || 0,
                    aantal: product.aantal,
                    opmerking: product.opmerking,
                    uitvoerDatum: product.uitvoer_datum,
                  },
                ];
                if (product.aangepaste_prijs !== null && product.aangepaste_prijs !== undefined) {
                  rows.push({
                    productType: product.product_type,
                    prijsType: "aangepast",
                    prijs: product.aangepaste_prijs,
                    aantal: product.aantal,
                    opmerking: product.opmerking,
                    uitvoerDatum: product.uitvoer_datum,
                  });
                }
                return rows;
              })
            : [];

          return {
            ...offerte,
            locatie: offerte.locaties ?? null,
            producten: products,
          };
        }) || [];

      const locatieIds = mappedData.map((offerte) => offerte.locatie?.id).filter((id): id is string => Boolean(id));

      if (locatieIds.length > 0) {
        const { data: fotosData, error: fotosError } = await supabase
          .schema("rapport_details")
          .from("fotos")
          .select("id, remote_url, context_label, locatie_id, updated_at, created_at")
          .in("locatie_id", locatieIds)
          .is("rapport_id", null)
          .order("updated_at", { ascending: false })
          .order("created_at", { ascending: false });

        if (fotosError) {
          console.error("Error fetching locatie fotos:", fotosError);
        } else if (fotosData) {
          // Rows are already sorted newest-first; first occurrence per locatie_id is the current version.
          const latestByLocatie = fotosData.reduce<Record<string, FotoBovenFotoRow>>((acc, foto) => {
            if (!foto.locatie_id || acc[foto.locatie_id]) return acc;
            acc[foto.locatie_id] = normalizeFotoBovenRow(foto) as FotoBovenFotoRow;
            return acc;
          }, {});

          mappedData.forEach((offerte) => {
            const locatieId = offerte.locatie?.id;
            if (locatieId && offerte.locatie) {
              const latest = latestByLocatie[locatieId];
              offerte.locatie.fotoBoven_foto = latest ? [latest] : [];
            }
          });
        }
      }

      offertes.value = mappedData as Offerte[];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      error.value = errorMessage;
      console.error("[offertes] fetchOffertes error", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const getOfferteById = async (id: string): Promise<Offerte | null> => {
    try {
      loading.value = true;
      error.value = null;

      const detailSelect: string = `
          id,
          offerteNr,
          offerteDatum,
          createdTime,
          korting,
          kortingBedrag,
          kortingPerc,
          totaalIncKorting,
          handTekening,
          basis,
          status,
          offerte_producten(
            product_type,
            voorgestelde_prijs,
            aangepaste_prijs,
            aantal,
            opmerking,
            uitvoer_datum
          ),
          voorwaarden,
          iplan,
          last_seen,
          testItem,
          bundel_id,
          offerte_bundels:bundel_id(id, naam, bundel_nr),
          locatie:locatie_id(
            id,
            naam,
            contactNaam,
            straat,
            huisnr,
            postcode,
            plaats,
            branche,
            contactMail,
            telnr,
            typeObject,
            bouwJaar,
            installatieJaar,
            pvInstallatieJaar,
            gebruiksDoel,
            created,
            locaties_opdrachtgevers!inner(
              is_default,
              opdrachtgever:opdrachtgever_id(
                id,
                naam,
                contactnaam,
                telnr,
                straat,
                huisnr,
                postcode,
                plaats,
                user:users!opdrachtgevers_id_fkey(
                  id,
                  email,
                  display_name,
                  role,
                  created_at
                )
              )
            )
          ),
          ibedrijf:ibedrijf_id(
            id,
            created_at,
            bic,
            btwnr,
            contactnaam,
            huisnr,
            iban,
            kvk,
            plaats,
            postcode,
            straat,
            telnr,
            website,
            scios_ids,
            inspecties,
            logo,
            scios_reg,
            users!inner(
              id,
              email,
              display_name,
              created_at
            )
          )
        `;

      const { data, error: queryError } = await supabase
        .from("offertes")
        .select(detailSelect)
        .eq("id", id)
        .eq("locatie.locaties_opdrachtgevers.is_default", true)
        .single();

      if (queryError) throw queryError;

      // Transform data: flatten locaties_opdrachtgevers to opdrachtgever for backward compatibility
      const detailData = data as unknown as OfferteDetailRow | null;

      if (detailData?.locatie && detailData.locatie.locaties_opdrachtgevers) {
        const defaultOpdrachtgever = detailData.locatie.locaties_opdrachtgevers[0]?.opdrachtgever;
        if (defaultOpdrachtgever) {
          detailData.locatie.opdrachtgever = defaultOpdrachtgever;
        }
        delete detailData.locatie.locaties_opdrachtgevers;
      }

      if (detailData?.locatie?.id) {
        const { data: fotosData, error: fotosError } = await supabase
          .schema("rapport_details")
          .from("fotos")
          .select("id, remote_url, context_label, locatie_id")
          .eq("locatie_id", detailData.locatie.id)
          .is("rapport_id", null)
          .order("updated_at", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(1);

        if (fotosError) {
          console.error("Error fetching locatie fotos:", fotosError);
        } else if (fotosData) {
          detailData.locatie.fotoBoven_foto = fotosData.map((foto) =>
            normalizeFotoBovenRow(foto),
          ) as FotoBovenFotoRow[];
        }
      }

      if (detailData) {
        (detailData as any).producten = Array.isArray((detailData as any).offerte_producten)
          ? (detailData as any).offerte_producten.flatMap((product: any) => {
              const rows = [
                {
                  productType: product.product_type,
                  prijsType: "voorgesteld",
                  prijs: product.voorgestelde_prijs || 0,
                  aantal: product.aantal,
                  opmerking: product.opmerking,
                  uitvoerDatum: product.uitvoer_datum,
                },
              ];
              if (product.aangepaste_prijs !== null && product.aangepaste_prijs !== undefined) {
                rows.push({
                  productType: product.product_type,
                  prijsType: "aangepast",
                  prijs: product.aangepaste_prijs,
                  aantal: product.aantal,
                  opmerking: product.opmerking,
                  uitvoerDatum: product.uitvoer_datum,
                });
              }
              return rows;
            })
          : [];
      }

      return detailData ? (detailData as Offerte) : null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      error.value = errorMessage;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const acceptOfferte = async (id: string, signature: string, inspectionDates?: Record<string, string | null>) => {
    try {
      loading.value = true;
      error.value = null;

      // Guard: prevent signing bundel-member offertes individually
      const { data: bundelCheck } = await supabase.from("offertes").select("bundel_id").eq("id", id).single();

      if (bundelCheck?.bundel_id) {
        throw new Error(
          "Deze offerte maakt deel uit van een bundel en kan niet individueel worden geaccepteerd. Gebruik de bundel-flow om alle offertes in de bundel te ondertekenen.",
        );
      }

      // First get the current offerte to preserve existing status history
      const { data: currentOfferte, error: fetchError } = await supabase
        .from("offertes")
        .select("status")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      // Write the chosen inspection dates onto the existing producten rows before marking
      // the offerte accepted, so a failure here can't leave a half-accepted offerte.
      // Opdrachtgevers only have UPDATE rights on offerte_producten (RLS blocks
      // insert/delete), so never rewrite the rows here.
      const inspectionTypes = ["scope10", "scope8ebi", "scope8pi", "scope12ebi", "scope12pi", "nen1010", "nen3140"];
      for (const [productType, inspectionDate] of Object.entries(inspectionDates ?? {})) {
        if (!inspectionDate || !inspectionTypes.includes(productType)) continue;

        const { error: dateError } = await supabase
          .from("offerte_producten")
          .update({ uitvoer_datum: inspectionDate })
          .eq("offerte_id", id)
          .eq("product_type", productType);

        if (dateError) throw dateError;
      }

      // Create new status array with "alles_geaccepteerd" status appended
      const currentStatus = (currentOfferte?.status as any[]) || [];
      const userEmail = authStore.user?.email || "opdrachtgever";
      const statusEntry: any = {
        status: "alles_geaccepteerd",
        datum: new Date().toISOString(),
        door: userEmail,
      };

      const newStatus = [...currentStatus, statusEntry];

      // Update the offerte with signature, accept date and status
      const { error: updateError } = await supabase
        .from("offertes")
        .update({
          handTekening: signature,
          accept_datum: new Date().toISOString(),
          status: newStatus,
        })
        .eq("id", id);

      if (updateError) throw updateError;

      // Refresh both the list and the single offerte. Notifications (ibedrijf
      // + opdrachtgever email) are enqueued by a DB trigger on the status
      // update itself and delivered by the process-notification-queue worker.
      await Promise.all([fetchOffertes(), getOfferteById(id)]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      error.value = errorMessage;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const rejectOfferte = async (id: string) => {
    try {
      loading.value = true;
      error.value = null;

      // Guard: prevent rejecting bundel-member offertes individually
      const { data: bundelCheck } = await supabase.from("offertes").select("bundel_id").eq("id", id).single();

      if (bundelCheck?.bundel_id) {
        throw new Error(
          "Deze offerte maakt deel uit van een bundel en kan niet individueel worden afgewezen. Gebruik de bundel-flow om alle offertes in de bundel te beheren.",
        );
      }

      const { data: currentOfferte, error: fetchError } = await supabase
        .from("offertes")
        .select("status")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      const currentStatus = (currentOfferte?.status as any[]) || [];
      const userEmail = authStore.user?.email || "opdrachtgever";
      const statusEntry: any = {
        status: "afgewezen",
        datum: new Date().toISOString(),
        door: userEmail,
      };

      const newStatus = [...currentStatus, statusEntry];

      const { error: updateError } = await supabase
        .from("offertes")
        .update({
          status: newStatus,
        })
        .eq("id", id);

      if (updateError) throw updateError;

      await Promise.all([fetchOffertes(), getOfferteById(id)]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      error.value = errorMessage;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Record that the opdrachtgever opened this offerte in the portal.
  // Fire-and-forget: tracking must never block or break the view.
  const markOfferteBekeken = async (id: string): Promise<void> => {
    try {
      const { error: rpcError } = await supabase.rpc("mark_offerte_bekeken", {
        p_offerte_id: id,
      });
      if (rpcError) throw rpcError;
    } catch (err) {
      console.error("Failed to mark offerte as bekeken:", err);
    }
  };

  // Future 'beschikbaar' days of the inspectiebureau (planning_events), via a
  // SECURITY DEFINER RPC: planning_events RLS only admits the ibedrijf itself.
  const fetchIbedrijfBeschikbaarheid = async (ibedrijfId: string): Promise<string[]> => {
    try {
      const { data, error: rpcError } = await supabase.rpc("get_ibedrijf_beschikbaarheid", {
        p_ibedrijf_id: ibedrijfId,
      });
      if (rpcError) throw rpcError;
      return data ?? [];
    } catch (err) {
      console.error("Failed to fetch ibedrijf beschikbaarheid:", err);
      return [];
    }
  };

  return {
    offertes,
    loading,
    error,
    totalCount,
    fetchOffertes,
    getOfferteById,
    acceptOfferte,
    rejectOfferte,
    markOfferteBekeken,
    fetchIbedrijfBeschikbaarheid,
  };
});
